import * as React from 'react';
import Box from '@mui/joy/Box';
import CircularProgress from '@mui/joy/CircularProgress';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import SavingsRoundedIcon from '@mui/icons-material/SavingsRounded';
import OutboxRoundedIcon from '@mui/icons-material/OutboxRounded';
import OnlinePredictionRoundedIcon from '@mui/icons-material/OnlinePredictionRounded';
import WifiRoundedIcon from '@mui/icons-material/WifiRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import PercentRoundedIcon from '@mui/icons-material/PercentRounded';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import { Link as RouterLink } from 'react-router-dom';
import Link from '@mui/joy/Link';
import { AdminGeneralInfoResult } from '../../../models/adminGeneral';
import { formatRubles } from '../../../common/money';

export type SummaryHeaderProps = {
    loading: boolean;
    data: AdminGeneralInfoResult | null;
};

export const SummaryHeader: React.FC<SummaryHeaderProps> = ({ loading, data }) => {
    const pct = (cur: number, prev: number) => {
        if (!prev) return 0;
        return ((cur - prev) / Math.abs(prev)) * 100;
    };
    const renderDelta = (current: number, previous: number) => {
        const value = pct(current, previous);
        if (!isFinite(value) || value === 0) {
            return (
                <Typography level="body-sm" sx={{ mt: 1 }}>0%</Typography>
            );
        }
        const isPositive = value > 0;
        const color = isPositive ? 'success.600' : 'danger.600';
        return (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 1, color }}>
                {isPositive ? <ArrowUpwardRoundedIcon fontSize="small"/> : <ArrowDownwardRoundedIcon fontSize="small"/>}
                <Typography level="body-sm" sx={{ color }}>
                    {value.toFixed(2)}%
                </Typography>
            </Box>
        );
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                <CircularProgress size="sm" />
            </Box>
        );
    }

    if (!data) {
        return <Typography level="title-md">Карточки</Typography>;
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title={'Профит'} disablePortal={false}>
                        <TrendingUpRoundedIcon
                            fontSize="small"
                            sx={{ color: (data && data.profitSum > data.profitSumLastPeriod) ? 'success.600' : 'danger.600' }}
                        />
                    </Tooltip>
                    <Tooltip title={`Профит относительно прошлого периода ${pct(data.profitSum, data.profitSumLastPeriod).toFixed(2)}%`} disablePortal={false}>
                        <Typography
                            level="title-md"
                            sx={{ color: (data && data.profitSum > data.profitSumLastPeriod) ? 'success.600' : 'danger.600' }}
                        >
                            {`+${formatRubles(data.profitSum)}`}
                        </Typography>
                    </Tooltip>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title={'Ордера'} disablePortal={false}>
                        <ReceiptLongRoundedIcon
                            fontSize="small"
                            sx={{ color: (data && (data.orderProfit ?? 0) > (data.orderProfitLastPeriod ?? 0)) ? 'success.600' : 'danger.600' }}
                        />
                    </Tooltip>
                    <Tooltip title={`Профит с ордеров относительно прошлого периода ${pct(data.orderProfit ?? 0, data.orderProfitLastPeriod ?? 0).toFixed(2)}%`} disablePortal={false}>
                        <Typography
                            level="title-md"
                            sx={{ color: (data && (data.orderProfit ?? 0) > (data.orderProfitLastPeriod ?? 0)) ? 'success.600' : 'danger.600' }}
                        >
                            {data ? `+${formatRubles(data.orderProfit ?? 0)}` : '-'}
                        </Typography>
                    </Tooltip>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title={'Депозиты'} disablePortal={false}>
                        <SavingsRoundedIcon
                            fontSize="small"
                            sx={{ color: (data && (data.depositProfit ?? 0) > (data.depositProfitLastPeriod ?? 0)) ? 'success.600' : 'danger.600' }}
                        />
                    </Tooltip>
                    <Tooltip title={`Профит с депозитов относительно прошлого периода ${pct(data.depositProfit ?? 0, data.depositProfitLastPeriod ?? 0).toFixed(2)}%`} disablePortal={false}>
                        <Typography
                            level="title-md"
                            sx={{ color: (data && (data.depositProfit ?? 0) > (data.depositProfitLastPeriod ?? 0)) ? 'success.600' : 'danger.600' }}
                        >
                            {data ? `+${formatRubles(data.depositProfit ?? 0)}` : '-'}
                        </Typography>
                    </Tooltip>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title={'Выводы'} disablePortal={false}>
                        <OutboxRoundedIcon
                            fontSize="small"
                            sx={{ color: (data && (data.withdrawalProfit ?? 0) > (data.withdrawalProfitLastPeriod ?? 0)) ? 'success.600' : 'danger.600' }}
                        />
                    </Tooltip>
                    <Tooltip title={`Профит с выводов относительно прошлого периода ${pct(data.withdrawalProfit ?? 0, data.withdrawalProfitLastPeriod ?? 0).toFixed(2)}%`} disablePortal={false}>
                        <Typography
                            level="title-md"
                            sx={{ color: (data && (data.withdrawalProfit ?? 0) > (data.withdrawalProfitLastPeriod ?? 0)) ? 'success.600' : 'danger.600' }}
                        >
                            {data ? `+${formatRubles(data.withdrawalProfit ?? 0)}` : '-'}
                        </Typography>
                    </Tooltip>
                </Box>

                <Tooltip title={'Чистая позиция по ликвидности = (Баланс Tome + Баланс GMoney) - Баланс сайта. Юнит-экономика → Float'} disablePortal={false}>
                    <Box
                        component={RouterLink}
                        to="/app/unit-economics#ue-payments-float"
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, textDecoration: 'none', color: 'inherit' }}
                    >
                        {(() => {
                            const net = (data?.tomeBalance ?? 0) + (data?.gameMoneyBalance ?? 0) - (data?.siteBalance ?? 0);
                            const color = net >= 0 ? 'success.600' : 'danger.600';
                            const formatted = formatRubles(Math.abs(net));
                            return (
                                <>
                                    <MonetizationOnIcon fontSize="small" sx={{ color }} />
                                    <Typography level="title-md" sx={{ color }}>
                                        {net >= 0 ? `+${formatted}` : `-${formatted}`}
                                    </Typography>
                                </>
                            );
                        })()}
                    </Box>
                </Tooltip>

                <Tooltip
                    title={
                        <Box sx={{ maxWidth: 280 }}>
                            Take Rate — доля комиссии от оборота (GMV) ордеров. Открыть юнит-экономику.
                            <Box sx={{ mt: 0.5 }}>
                                <Link
                                    href="https://ru.wikipedia.org/wiki/%D0%9A%D0%BE%D0%BC%D0%B8%D1%81%D1%81%D0%B8%D1%8F"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    level="body-xs"
                                    sx={{ color: "primary.200" }}
                                >
                                    Википедия: комиссия
                                </Link>
                            </Box>
                        </Box>
                    }
                    disablePortal={false}
                >
                    <Box
                        component={RouterLink}
                        to="/app/unit-economics#ue-overview"
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, textDecoration: 'none', color: 'inherit' }}
                    >
                        <PercentRoundedIcon fontSize="small" sx={{ color: 'primary.600' }} />
                        <Typography level="title-md" sx={{ color: 'primary.600' }}>
                            {(() => {
                                const gmv = data?.orderSum ?? 0;
                                const fee = data?.orderProfit ?? 0;
                                if (!gmv) return 'Take —';
                                return `Take ${(fee / gmv * 100).toFixed(1)}%`;
                            })()}
                        </Typography>
                    </Box>
                </Tooltip>

                <Tooltip title={'Раздел юнит-экономики'} disablePortal={false}>
                    <Link
                        component={RouterLink}
                        to="/app/unit-economics#ue-overview"
                        level="title-md"
                        startDecorator={<QueryStatsIcon fontSize="small" />}
                        sx={{ textDecoration: 'none' }}
                    >
                        UE
                    </Link>
                </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Tooltip title={'Онлайн пользователи'} disablePortal={false}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <WifiRoundedIcon fontSize="small" sx={{ color: 'danger.600' }} />
                        <Typography level="title-md" sx={{ color: 'danger.600' }}>{data?.online ?? '-'}</Typography>
                    </Box>
                </Tooltip>
                <Tooltip title={'Онлайн авторизованные'} disablePortal={false}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <VerifiedUserRoundedIcon fontSize="small" sx={{ color: 'primary.600' }} />
                        <Typography level="title-md" sx={{ color: 'primary.600' }}>{data?.onlineLoggedIn ?? '-'}</Typography>
                    </Box>
                </Tooltip>
                <Tooltip title={'Онлайн активные (авторизованные)'} disablePortal={false}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <OnlinePredictionRoundedIcon fontSize="small" sx={{ color: 'success.600' }} />
                        <Typography level="title-md" sx={{ color: 'success.600' }}>{data?.onlineLoggedInActive ?? '-'}</Typography>
                    </Box>
                </Tooltip>
            </Box>
        </Box>
    );
};


